import axios from "axios";
import useUserStore from "@/store/userStore";
import { notification } from "ant-design-vue";
import FileSaver from "file-saver";
import emitter from "@/utils/eventBus";

// 使用环境变量
const apiUrl = import.meta.env.VITE_API_URL;

export const request = axios.create({
  baseURL: apiUrl, // api base_url
  timeout: 1000 * 5, // 请求超时时间
});

// 是否正在刷新 Token 的标记
let isRefreshing = false;
// 重试队列（存储待重试的请求）
let retryQueue = [];

request.interceptors.request.use(
  (config) => {
    const userStore = useUserStore();
    const token = userStore.getToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    // 对请求错误做些什么
    return Promise.reject(error);
  },
);

request.interceptors.response.use(
  (response) => {
    if (response.headers["content-type"] === "application/json") {
      return response.data;
    } else {
      return response;
    }
  },
  async (error) => {
    if (error.response) {
      const userStore = useUserStore();
      let data = error.response.data;
      switch (error.response.status) {
        case 401: {
          const originalRequest = error.config;
          if (originalRequest._retry) {
            return Promise.reject(error);
          }
          if (isRefreshing) {
            return new Promise((resolve) => {
              retryQueue.push(() => resolve(request(originalRequest)));
            });
          }

          // 标记开始刷新
          isRefreshing = true;
          originalRequest._retry = true;
          try {
            const refreshTokenSuccess = await userStore.refreshToken();
            if (refreshTokenSuccess) {
              retryQueue.forEach((cb) => cb());
              retryQueue = [];
            }
            return request(originalRequest);
            // eslint-disable-next-line no-unused-vars
          } catch (e) {
            notification.error({
              message: "系统提示",
              description: "未认证或登录已过期，请重新登录",
            });
            emitter.emit("request:401");
          } finally {
            isRefreshing = false;
          }
          break;
        }
        case 403:
          notification.error({
            message: "系统提示",
            description: "拒绝访问",
          });
          break;
        case 404:
          notification.error({
            message: "系统提示",
            description: "资源未找到",
          });
          break;
        case 500:
          notification.error({
            message: "系统提示",
            description: "内部错误",
          });
          break;
        case 504:
          notification.error({
            message: "系统提示",
            description: "请求超时",
          });
          break;
        default:
          notification.error({
            message: "系统提示",
            description: data.message || "内部错误",
            duration: 4,
          });
      }
    }
    return Promise.reject(error);
  },
);

//post
export function postRequest(url, parameter) {
  return request({
    url: url,
    method: "post",
    data: parameter,
  });
}

//post method= {post | put}
export function httpRequest(url, parameter, method) {
  return request({
    url: url,
    method: method,
    data: parameter,
  });
}

//put
export function putRequest(url, parameter) {
  return request({
    url: url,
    method: "put",
    data: parameter,
  });
}

//get
export function getRequest(url, parameter) {
  return request({
    url: url,
    method: "get",
    params: parameter,
  });
}

//deleteRequest
export function deleteRequest(url, parameter) {
  return request({
    url: url,
    method: "delete",
    params: parameter,
  });
}

export function exportFile(
  url,
  parameter,
  fileName = "导出文件",
  method = "get",
) {
  return new Promise((resolve, reject) => {
    request({
      url: url,
      method: method,
      params: parameter,
      responseType: "blob",
    })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        FileSaver.saveAs(blob, fileName);
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export default {
  request,
};
